<script setup>
import { onMounted, reactive } from "vue"
import $ from "jquery"
import "datatables.net"
import "datatables.net-bs5"
import "datatables.net-bs5/css/dataTables.bootstrap5.css"
import api from "@/api/axios.js"

// Visor de las dos tablas que ya arma auditLog.js en cada request
// autenticado (ActivityLog) y en eventos de seguridad puntuales
// (SecurityEvent) — quién hizo qué, desde dónde y cuándo. Ambos endpoints
// ya existían y estaban protegidos (admin/supervisor); lo que faltaba era
// esta pantalla para poder consultarlos.

const filtrosActividad = reactive({ userId: "", desde: "", hasta: "", ruta: "" })
const filtrosSeguridad = reactive({ userId: "", desde: "", hasta: "", eventType: "" })

const dataTableLanguage = {
  decimal: "",
  emptyTable: "No hay datos disponibles en la tabla",
  info: "Mostrando _START_ a _END_ de _TOTAL_ registros",
  infoEmpty: "Mostrando 0 a 0 de 0 registros",
  infoFiltered: "(filtrado de _MAX_ registros totales)",
  lengthMenu: "Mostrar _MENU_ registros",
  loadingRecords: "Cargando...",
  processing: "Procesando...",
  search: "Buscar:",
  zeroRecords: "No se encontraron resultados",
  paginate: { first: "<<", last: ">>", next: ">", previous: "<" }
}

function formatFecha(value) {
  if (!value) return ""
  return new Date(value).toLocaleString("es-CO")
}

let tablaActividad = null
let tablaSeguridad = null

onMounted(() => {
  tablaActividad = $("#activityLogTable").DataTable({
    serverSide: true,
    stateSave: false,
    pageLength: 25,
    lengthMenu: [25, 50, 100, 200],
    order: [[0, "desc"]],
    ajax: async function (data, callback) {
      try {
        const res = await api.get("/logs/actividad", {
          params: { ...data, ...filtrosActividad }
        })
        callback(res.data)
      } catch (err) {
        console.error(err)
        callback({ draw: data.draw, recordsTotal: 0, recordsFiltered: 0, data: [] })
      }
    },
    columns: [
      { data: "createdAt", render: (d) => formatFecha(d) },
      { data: null, render: (d, t, row) => row.usuario || row.email || row.userId || "—" },
      { data: "role", defaultContent: "—" },
      { data: "method" },
      { data: "route" },
      { data: "statusCode" },
      { data: "ip", defaultContent: "—" },
      { data: "durationMs", defaultContent: "—" }
    ],
    language: dataTableLanguage
  })

  tablaSeguridad = $("#securityEventTable").DataTable({
    serverSide: true,
    stateSave: false,
    pageLength: 25,
    lengthMenu: [25, 50, 100, 200],
    order: [[0, "desc"]],
    ajax: async function (data, callback) {
      try {
        const res = await api.get("/logs/seguridad", {
          params: { ...data, ...filtrosSeguridad }
        })
        callback(res.data)
      } catch (err) {
        console.error(err)
        callback({ draw: data.draw, recordsTotal: 0, recordsFiltered: 0, data: [] })
      }
    },
    columns: [
      { data: "createdAt", render: (d) => formatFecha(d) },
      { data: null, render: (d, t, row) => row.usuario || row.email || row.userId || "—" },
      { data: "eventType" },
      { data: "ip", defaultContent: "—" },
      { data: "details", render: (d) => (d ? JSON.stringify(d) : "—") }
    ],
    language: dataTableLanguage
  })
})

function filtrarActividad() {
  tablaActividad?.ajax.reload()
}

function filtrarSeguridad() {
  tablaSeguridad?.ajax.reload()
}
</script>

<template>
  <div>
    <div class="card shadow-sm mb-4">
      <div class="card-header pb-0">
        <h5 class="mb-0"><i class="bi bi-list-ul me-2"></i>Actividad (todas las peticiones autenticadas)</h5>
        <p class="text-sm text-muted mb-0">
          Quién consultó/editó qué ruta y cuándo. Para ver accesos a un módulo puntual, filtra por ruta
          (ej. "empleados", "users", "quotes").
        </p>
      </div>
      <div class="card-body">
        <div class="row g-2 mb-3">
          <div class="col-md-2">
            <input v-model="filtrosActividad.userId" type="number" class="form-control form-control-sm" placeholder="ID de usuario">
          </div>
          <div class="col-md-3">
            <input v-model="filtrosActividad.ruta" type="text" class="form-control form-control-sm" placeholder="Ruta contiene... (empleados, users, quotes)">
          </div>
          <div class="col-md-2">
            <input v-model="filtrosActividad.desde" type="date" class="form-control form-control-sm">
          </div>
          <div class="col-md-2">
            <input v-model="filtrosActividad.hasta" type="date" class="form-control form-control-sm">
          </div>
          <div class="col-md-2">
            <button type="button" class="btn btn-sm btn-dark w-100" @click="filtrarActividad">Filtrar</button>
          </div>
        </div>

        <div class="table-responsive">
          <table id="activityLogTable" class="table table-striped table-bordered align-middle w-100">
            <thead class="table-dark">
              <tr>
                <th>Fecha</th>
                <th>Usuario</th>
                <th>Rol</th>
                <th>Método</th>
                <th>Ruta</th>
                <th>Status</th>
                <th>IP</th>
                <th>Duración (ms)</th>
              </tr>
            </thead>
            <tbody></tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="card-header pb-0">
        <h5 class="mb-0"><i class="bi bi-shield-lock me-2"></i>Eventos de seguridad</h5>
        <p class="text-sm text-muted mb-0">
          Logins, cambios de contraseña/rol, accesos denegados y revelado de datos sensibles (cédula, teléfono).
        </p>
      </div>
      <div class="card-body">
        <div class="row g-2 mb-3">
          <div class="col-md-2">
            <input v-model="filtrosSeguridad.userId" type="number" class="form-control form-control-sm" placeholder="ID de usuario">
          </div>
          <div class="col-md-3">
            <input v-model="filtrosSeguridad.eventType" type="text" class="form-control form-control-sm" placeholder="Tipo de evento (ej. LOGIN_FAILED)">
          </div>
          <div class="col-md-2">
            <input v-model="filtrosSeguridad.desde" type="date" class="form-control form-control-sm">
          </div>
          <div class="col-md-2">
            <input v-model="filtrosSeguridad.hasta" type="date" class="form-control form-control-sm">
          </div>
          <div class="col-md-2">
            <button type="button" class="btn btn-sm btn-dark w-100" @click="filtrarSeguridad">Filtrar</button>
          </div>
        </div>

        <div class="table-responsive">
          <table id="securityEventTable" class="table table-striped table-bordered align-middle w-100">
            <thead class="table-dark">
              <tr>
                <th>Fecha</th>
                <th>Usuario</th>
                <th>Evento</th>
                <th>IP</th>
                <th>Detalles</th>
              </tr>
            </thead>
            <tbody></tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>
